import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function OrgSettingsModal({ open, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.uid === 'U-0000';
  const [loading, setLoading] = useState(false);
  const [editable, setEditable] = useState([]);
  const [values, setValues] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tree = await api.get('/organizations/tree');
      const flat = [];
      const walk = (nodes) => {
        for (const n of nodes) {
          flat.push(n);
          if (n.children) walk(n.children);
        }
      };
      walk(Array.isArray(tree) ? tree : []);

      const ledByMe = new Set(flat.filter(o => o.leader_user_id === user?.id).map(o => o.id));
      const list = isAdmin
        ? flat
        : flat.filter(o => ledByMe.has(o.id) || ledByMe.has(o.parent_id));

      list.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      setEditable(list);
      setValues(Object.fromEntries(list.map(o => [o.id, o.min_margin_rate == null ? '' : String(o.min_margin_rate)])));
      setMsg({});
    } catch (e) {
      alert('조직 목록을 불러올 수 없습니다: ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdmin]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveRow = async (org) => {
    setSavingId(org.id);
    setMsg(m => ({ ...m, [org.id]: '' }));
    const raw = values[org.id];
    const payload = { min_margin_rate: raw === '' || raw == null ? null : Number(raw) };
    try {
      const updated = await api.put(`/organizations/${org.id}/min-margin-rate`, payload);
      setEditable(list => list.map(o => o.id === org.id ? { ...o, min_margin_rate: updated.min_margin_rate } : o));
      setMsg(m => ({ ...m, [org.id]: 'ok' }));
      setTimeout(() => setMsg(m => ({ ...m, [org.id]: '' })), 1500);
    } catch (e) {
      alert(e.message || '저장 실패');
    } finally {
      setSavingId(null);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:14, padding:24, width:520, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ fontSize:16, fontWeight:700, marginBottom:12, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
          <span>조직 설정 — 최저 공헌이익율</span>
          <span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={onClose}>✕</span>
        </div>

        <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', fontSize:11, color:'#64748b', lineHeight:1.7, marginBottom:14 }}>
          소속원이 작성한 견적의 공헌이익율이 이 값 미만이면 승인 절차가 시작됩니다.<br/>
          하위 조직 값은 상위 조직 값 이상이어야 하며, 비우면 위임(상위 단계 임계값 적용)됩니다.
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:30, color:'#94a3b8', fontSize:13 }}>로딩 중...</div>
        ) : editable.length === 0 ? (
          <div style={{ textAlign:'center', padding:30, color:'#94a3b8', fontSize:13 }}>
            관리하는 조직이 없습니다.<br/>조직장만 최저 공헌이익율을 설정할 수 있습니다.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {editable.map(org => (
              <div key={org.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:'#fafbfc', borderRadius:8, border:'1px solid #e2e8f0' }}>
                <div style={{ flex:1, fontSize:13, fontWeight:600 }}>{org.name}</div>
                <input
                  type="number" min={0} max={100} step="0.01"
                  className="form-input"
                  style={{ width:90, padding:'6px 8px', fontSize:13 }}
                  value={values[org.id] ?? ''}
                  placeholder="위임"
                  onChange={e => setValues(v => ({ ...v, [org.id]: e.target.value }))}
                />
                <span style={{ fontSize:12, color:'#64748b' }}>%</span>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={savingId === org.id}
                  onClick={() => saveRow(org)}
                >{savingId === org.id ? '저장 중...' : '저장'}</button>
                {msg[org.id] === 'ok' && <span style={{ fontSize:11, color:'#16a34a', fontWeight:600 }}>✓</span>}
              </div>
            ))}
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
          <button className="btn btn-outline" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
